/**
 * The settings rail's search: state, the pinned header, and the result list.
 *
 * Extracted because `SettingsSidebarNav.tsx` crossed the 300-line extract-first
 * rule in `.agents/rules/frontend-architecture.md` the moment search landed --
 * it went 357 -> 630 by absorbing a hook, three components and the keyboard
 * logic. The nav file keeps the nav; this file keeps the search.
 */
import { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeftIcon, SearchIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { isElectron } from "../../env";
import { SidebarGroup, SidebarMenu, SidebarMenuButton, SidebarMenuItem } from "../ui/sidebar";
import { SETTINGS_SECTION_LABELS, type SettingsSearchItem } from "./settingsSearch";
import { searchSettings } from "./settingsSearchQuery";
import {
  buildSearchOptions,
  nextResultIndex,
  searchKeyAction,
  type SettingsSearchOption,
} from "./settingsSearchModel";

// Re-exported for the rail's callers. `SettingsSearchSection` is deliberately
// NOT re-exported: nothing imports it from here, and forwarding it made a dead
// public export -- the same thing that happened with `SettingsSectionPath`
// during the previous split. Import it from `settingsSearchModel` directly.
export { buildSearchOptions, nextResultIndex, searchKeyAction, type SettingsSearchOption };

/**
 * Row-level search state: query, selection, keyboard.
 *
 * Section search (`searchSettingsNavGroups`) answers "which page is this on";
 * this answers "where is the toggle called X". They share one input and one
 * result list -- different indexes, not different features. `openItem` comes
 * from the caller so this hook stays ignorant of routing.
 */
export function useSettingsRowSearch(
  query: string,
  cmmdSyncEnabled: boolean,
  openItem: (item: SettingsSearchItem) => void,
) {
  const [activeResultIndex, setActiveResultIndex] = useState(0);
  const results = useMemo(
    () => searchSettings(query, { cmmdSyncEnabled, isDesktop: isElectron }),
    [cmmdSyncEnabled, query],
  );

  const open = useCallback(
    (item: SettingsSearchItem) => {
      openItem(item);
    },
    [openItem],
  );

  return { activeResultIndex, open, results, setActiveResultIndex };
}

/**
 * The pinned block: an icon-only Back control inline with search.
 *
 * No rule between them -- a hairline there reads as two separate pieces of
 * chrome rather than one header. The rule sits on this component's outer
 * border instead, marking the boundary that is real: pinned above, scrolling
 * below.
 */
export function SettingsRailHeader({
  activeResultId,
  onBack,
  onKeyDown,
  query,
  searchInputRef,
  setQuery,
}: {
  readonly activeResultId: string | undefined;
  readonly onBack: () => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  readonly query: string;
  readonly searchInputRef: React.RefObject<HTMLInputElement | null>;
  readonly setQuery: (value: string) => void;
}) {
  return (
    <div className="border-b border-sidebar-border">
      <SidebarGroup className="p-[var(--sidebar-content-inset)]">
        <div
          className="flex w-full min-w-0 items-center gap-2"
          data-testid="settings-rail-header-row"
        >
          <SidebarMenuButton
            aria-label="Back"
            size="icon"
            tooltip="Back"
            className="w-auto shrink-0 text-sidebar-muted-foreground hover:text-sidebar-foreground"
            onClick={onBack}
          >
            <ArrowLeftIcon className="size-4" />
          </SidebarMenuButton>
          {/* `min-h-touch`, not `min-h-11`: 11 resolves to 44px at the default
            root and less as the interface size drops, which is under the 48px
            contract on every phone. */}
          <div className="flex min-h-touch min-w-0 flex-1 items-center gap-2 rounded-md px-[var(--sidebar-row-content-inset)] focus-within:bg-sidebar-row-hover hover:bg-sidebar-row-hover md:min-h-8">
            <SearchIcon className="size-4 shrink-0 text-sidebar-muted-foreground md:size-3.5" />
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Search settings"
              placeholder="Search settings"
              // Restored after the merge reconciliation dropped them: arrowing
              // through results moves only React state, so without these a
              // screen reader is told nothing about which option is active.
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={activeResultId !== undefined}
              aria-controls={activeResultId !== undefined ? "settings-search-results" : undefined}
              aria-activedescendant={activeResultId}
              data-testid="settings-nav-search"
              className={cn(
                "min-w-0 flex-1 bg-transparent text-sidebar-foreground outline-none placeholder:text-sidebar-muted-foreground",
                // Under 16px, iOS Safari zooms the page on focus and does not
                // zoom back out, leaving the rail off-screen. Written out rather
                // than composed from NO_ZOOM_FIELD_TEXT_CLASS: Tailwind scans
                // source text, so a template literal generates no rule at all
                // and still reads as correct.
                "text-no-zoom md:text-[13px]",
              )}
            />
          </div>
        </div>
      </SidebarGroup>
    </div>
  );
}

/** Individual settings, under the sections, each captioned with its page. */
/**
 * Keep the keyboard-selected result visible.
 *
 * Focus never leaves the pinned input, so neither the browser nor the
 * ScrollArea scrolls for us: on a broad query the highlight walks off the
 * bottom of the rail while Enter still opens it, and a sighted keyboard user
 * loses track of what they are about to choose. `block: "nearest"` moves the
 * list only when the option is actually out of view, so ordinary arrowing
 * inside the visible window does not jump the rail around.
 */
function useActiveResultVisible(activeId: string | undefined, hasNavigated: boolean): void {
  useEffect(() => {
    // Only after an arrow key. On mount `activeResultIndex` is already 0, so
    // scrolling unconditionally yanked the rail down to the first ROW the
    // instant results appeared -- past the section hits rendered above it,
    // which is exactly the coarser answer the section-first ordering exists to
    // show. Scrolling is a response to keyboard navigation, not to results
    // existing.
    if (!hasNavigated || activeId === undefined) return;
    document.getElementById(activeId)?.scrollIntoView({ block: "nearest" });
  }, [activeId, hasNavigated]);
}

/**
 * Every match, sections and rows, inside ONE controlled listbox.
 *
 * They have to share a listbox, not merely a keyboard index. `aria-controls`
 * names a single element and `aria-activedescendant` must point INSIDE it, so
 * highlighting a section that lived among the ordinary nav buttons pointed
 * outside the controlled region -- and on a section-only query the listbox was
 * not rendered at all, leaving the combobox describing a set that did not
 * exist. Rendering both kinds here is what makes the highlight meaningful to a
 * screen reader rather than merely visible.
 */
export function SettingsSearchResults({
  activeOptionId,
  hasNavigated,
  onActivate,
  onOpenRow,
  onOpenSection,
  options,
}: {
  readonly activeOptionId: string | undefined;
  readonly hasNavigated: boolean;
  readonly onActivate: (index: number) => void;
  readonly onOpenRow: (item: SettingsSearchItem) => void;
  readonly onOpenSection: (to: string) => void;
  readonly options: ReadonlyArray<SettingsSearchOption>;
}) {
  useActiveResultVisible(activeOptionId, hasNavigated);

  if (options.length === 0) return null;

  return (
    <SidebarGroup className="p-[var(--sidebar-content-inset)]">
      <SidebarMenu id="settings-search-results" role="listbox">
        {options.map((option, index) => {
          const selected = option.id === activeOptionId;
          const Icon = option.kind === "section" ? option.section.icon : undefined;
          return (
            <SidebarMenuItem
              key={option.id}
              // Kept on section options so the narrowing contract FOR-2582
              // encoded still holds: typing filters the sections shown, and
              // its tests select on this attribute. Moving sections into the
              // listbox changed where they live, not what they do.
              {...(option.kind === "section"
                ? { "data-settings-nav-item": option.section.to }
                : {})}
            >
              <SidebarMenuButton
                size="sm"
                id={option.id}
                role="option"
                aria-selected={selected}
                isActive={selected}
                className={
                  option.kind === "section"
                    ? "gap-2.5 px-[var(--sidebar-row-content-inset)] py-2 text-left text-[13px]"
                    : // `md:h-auto` is load-bearing: `size="sm"` contributes `md:h-7`, a FIXED
                      // 28px, and the shared button is `overflow-hidden` -- so without
                      // this the second line (the page caption, which is the whole
                      // reason the row exists) is clipped away on every desktop.
                      "h-auto min-h-touch flex-col items-start justify-center gap-0 px-[var(--sidebar-row-content-inset)] py-2 text-left md:h-auto md:min-h-0"
                }
                onClick={() => {
                  if (option.kind === "section") onOpenSection(option.section.to);
                  else onOpenRow(option.item);
                }}
                onPointerMove={() => onActivate(index)}
              >
                {option.kind === "section" && Icon ? (
                  <>
                    <Icon className="size-4 shrink-0 text-[color:var(--sidebar-icon-color)]" />
                    <span className="min-w-0 flex-1 truncate">{option.section.label}</span>
                    <span className="shrink-0 text-[11px] text-sidebar-muted-foreground">Page</span>
                  </>
                ) : option.kind === "row" ? (
                  <>
                    <span className="w-full truncate text-[13px] text-sidebar-foreground">
                      {option.item.title}
                    </span>
                    {/* Which page it lives on is the whole reason to search. */}
                    <span className="w-full truncate text-[11px] text-sidebar-muted-foreground">
                      {SETTINGS_SECTION_LABELS[option.item.to]}
                    </span>
                  </>
                ) : null}
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
